import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError, Subject } from 'rxjs';
import {
  map,
  catchError,
  tap,
  debounceTime,
  take,
  filter,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface WishlistItem {
  wishlist_id: string;
  wishlisted_at: string;
  listing: {
    id: string;
    title: string;
    price: number;
    currency: string;
    status: string;
    created_at: string;
    updated_at: string;
    images: string[];
    has_images: boolean;
    image_count: number;
    first_image: string | null;
    table_name: string;
    description?: string;
    location?: string;
    is_featured?: boolean;
    [key: string]: any;
  };
}

export interface WishlistResponse {
  status: string;
  data: {
    items: WishlistItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface WishlistSummary {
  total: number;
  by_category: {
    [category: string]: number;
  };
}

export interface OptimizedWishlistStatus {
  [listingId: string]: string;
}

export interface WishlistStatus {
  [listingId: string]: {
    in_wishlist: boolean;
    wishlist_id: string | null;
  };
}

export interface WishlistToggleResponse {
  status: string;
  message: string;
  data: {
    success: boolean;
    action: 'added' | 'removed';
    in_wishlist: boolean;
    wishlist_id?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class WishlistService {
  private apiUrl = environment.apiUrl;
  private wishlistStatusSubject = new BehaviorSubject<WishlistStatus>({});
  private wishlistSummarySubject = new BehaviorSubject<WishlistSummary>({
    total: 0,
    by_category: {},
  });

  private readonly STORAGE_KEY = 'marketplace_wishlist_status';
  private readonly SUMMARY_STORAGE_KEY = 'marketplace_wishlist_summary';
  private readonly CACHE_EXPIRY_KEY = 'marketplace_wishlist_cache_expiry';
  private readonly CACHE_DURATION = 30 * 60 * 1000;

  private summaryCache$?: Observable<WishlistSummary>;
  private lastSummaryLoad = 0;
  private readonly SUMMARY_CACHE_DURATION = 10 * 60 * 1000;
  private summaryLoadSubject = new Subject<boolean>();

  public wishlistStatus$ = this.wishlistStatusSubject.asObservable();
  public wishlistSummary$ = this.wishlistSummarySubject.asObservable();

  private summaryLoaded = false;
  private summaryLoading = false;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.loadCachedWishlistData();

    this.summaryLoadSubject
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((forceRefresh) => {
        this.performSummaryLoad(forceRefresh);
      });

    this.authService.currentUser$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          return (prev?.id || null) === (curr?.id || null);
        })
      )
      .subscribe((user) => {
        if (user) {
          this.loadCachedSummary();
        } else {
          this.clearWishlistData();
        }
      });

    this.setupCacheCleanup();
  }

  private loadCachedSummary(): void {
    try {
      const cachedSummary = localStorage.getItem(this.SUMMARY_STORAGE_KEY);
      if (cachedSummary) {
        const summary = JSON.parse(cachedSummary);
        this.wishlistSummarySubject.next(summary);
        this.summaryLoaded = true;
      }
    } catch (error) {
      console.warn('Failed to load cached summary:', error);
    }
  }

  addToWishlist(listingId: string, tableName: string): Observable<any> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    const requestBody = {
      listing_id: listingId,
      table_name: tableName,
    };

    this.updateLocalWishlistStatus(listingId, {
      in_wishlist: true,
      wishlist_id: 'temp',
    });

    return this.http.post<any>(`${this.apiUrl}/wishlist`, requestBody).pipe(
      tap((response) => {
        this.updateLocalWishlistStatus(listingId, {
          in_wishlist: true,
          wishlist_id: response.data?.wishlist_id || 'temp',
        });
        this.incrementSummaryCount();
      }),
      catchError((error) => {
        this.updateLocalWishlistStatus(listingId, {
          in_wishlist: false,
          wishlist_id: null,
        });
        return this.handleError(error, 'Failed to add item to wishlist');
      })
    );
  }

  removeFromWishlist(listingId: string, tableName: string): Observable<any> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    const requestBody = {
      listing_id: listingId,
      table_name: tableName,
    };

    const previousState = this.getWishlistStatus(listingId);

    this.updateLocalWishlistStatus(listingId, {
      in_wishlist: false,
      wishlist_id: null,
    });

    return this.http
      .request('DELETE', `${this.apiUrl}/wishlist`, { body: requestBody })
      .pipe(
        tap(() => {
          this.decrementSummaryCount();
        }),
        catchError((error) => {
          this.updateLocalWishlistStatus(listingId, previousState);
          return this.handleError(error, 'Failed to remove item from wishlist');
        })
      );
  }

  toggleWishlist(
    listingId: string,
    tableName: string
  ): Observable<WishlistToggleResponse> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    const requestBody = {
      listing_id: listingId,
      table_name: tableName,
    };

    const currentStatus = this.getWishlistStatus(listingId);
    const newStatus = !currentStatus.in_wishlist;

    this.updateLocalWishlistStatus(listingId, {
      in_wishlist: newStatus,
      wishlist_id: newStatus ? 'temp' : null,
    });

    return this.http
      .put<WishlistToggleResponse>(
        `${this.apiUrl}/wishlist/toggle`,
        requestBody
      )
      .pipe(
        tap((response) => {
          this.updateLocalWishlistStatus(listingId, {
            in_wishlist: response.data.in_wishlist,
            wishlist_id: response.data.wishlist_id || null,
          });

          if (response.data.action === 'added') {
            this.incrementSummaryCount();
          } else {
            this.decrementSummaryCount();
          }
        }),
        catchError((error) => {
          this.updateLocalWishlistStatus(listingId, currentStatus);
          return this.handleError(error, 'Failed to toggle wishlist status');
        })
      );
  }

  getWishlistSummary(forceRefresh = false): Observable<WishlistSummary> {
    if (!this.authService.isLoggedIn()) {
      return of({ total: 0, by_category: {} });
    }

    const now = Date.now();
    const currentSummary = this.wishlistSummarySubject.value;

    if (
      !forceRefresh &&
      this.summaryLoaded &&
      now - this.lastSummaryLoad < this.SUMMARY_CACHE_DURATION
    ) {
      return of(currentSummary);
    }

    if (!forceRefresh && currentSummary.total > 0) {
      setTimeout(() => {
        this.summaryLoadSubject.next(false);
      }, 0);
      return of(currentSummary);
    }

    this.summaryLoadSubject.next(forceRefresh);

    return this.wishlistSummary$.pipe(
      filter((summary) => this.summaryLoaded),
      take(1)
    );
  }

  private performSummaryLoad(forceRefresh: boolean): void {
    if (this.summaryLoading && !forceRefresh) return;

    this.summaryLoading = true;
    this.lastSummaryLoad = Date.now();

    this.summaryCache$ = this.http
      .get<{ status: string; data: WishlistSummary }>(
        `${this.apiUrl}/wishlist/summary`
      )
      .pipe(
        map((response) => response.data),
        tap((summary) => {
          this.wishlistSummarySubject.next(summary);
          this.saveSummaryToCache(summary);
          this.summaryLoading = false;
          this.summaryLoaded = true;
          this.wishlistSummarySubject.next(summary);
        }),
        catchError((error) => {
          console.warn('Failed to fetch wishlist summary:', error);
          this.summaryLoading = false;
          this.summaryLoaded = true;
          const emptySummary = this.wishlistSummarySubject.value;
          this.wishlistSummarySubject.next(emptySummary);
          return of(emptySummary);
        }),
        shareReplay(1)
      );

    this.summaryCache$.subscribe();
  }

  private incrementSummaryCount(): void {
    const currentSummary = this.wishlistSummarySubject.value;
    const updatedSummary = {
      ...currentSummary,
      total: currentSummary.total + 1,
    };
    this.wishlistSummarySubject.next(updatedSummary);
    this.saveSummaryToCache(updatedSummary);
  }

  private decrementSummaryCount(): void {
    const currentSummary = this.wishlistSummarySubject.value;
    const updatedSummary = {
      ...currentSummary,
      total: Math.max(0, currentSummary.total - 1),
    };
    this.wishlistSummarySubject.next(updatedSummary);
    this.saveSummaryToCache(updatedSummary);
  }

  getUserWishlist(
    page: number = 1,
    limit: number = 20
  ): Observable<WishlistResponse> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    return this.http
      .get<WishlistResponse>(`${this.apiUrl}/wishlist`, {
        params: { page: page.toString(), limit: limit.toString() },
      })
      .pipe(
        tap((response) => {
          const freshStatus: WishlistStatus = {};
          response.data.items.forEach((item) => {
            freshStatus[item.listing.id] = {
              in_wishlist: true,
              wishlist_id: item.wishlist_id,
            };
          });
          this.updateMultipleWishlistStatus(freshStatus);
        }),
        catchError((error) =>
          this.handleError(error, 'Failed to fetch wishlist')
        )
      );
  }

  checkWishlistStatus(
    listings: Array<{ id: string; table_name: string }>
  ): Observable<WishlistStatus> {
    if (!this.authService.isLoggedIn()) {
      return of({});
    }

    if (!listings || listings.length === 0) {
      return of({});
    }

    const cachedResults: WishlistStatus = {};
    const uncachedListings: Array<{ id: string; table_name: string }> = [];

    listings.forEach((listing) => {
      const cached = this.getWishlistStatus(listing.id);
      if (this.isCacheValid(listing.id)) {
        cachedResults[listing.id] = cached;
      } else {
        uncachedListings.push(listing);
      }
    });

    if (uncachedListings.length === 0) {
      return of(cachedResults);
    }

    const requestBody = { listings: uncachedListings };

    return this.http
      .post<{ status: string; data: WishlistStatus }>(
        `${this.apiUrl}/wishlist/check`,
        requestBody
      )
      .pipe(
        map((response) => ({ ...cachedResults, ...response.data })),
        tap((wishlistStatus) => {
          this.updateMultipleWishlistStatus(wishlistStatus);
          this.updateCacheTimestamp();
        }),
        catchError((error) => {
          console.warn('Failed to check wishlist status:', error);
          return of(cachedResults);
        })
      );
  }

  getWishlist(
    category: string | null = null,
    page: number = 1,
    limit: number = 12
  ): Observable<{
    items: WishlistItem[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    let params = `page=${page}&limit=${limit}`;
    if (category) {
      params += `&category=${category}`;
    }

    return this.http
      .get<WishlistResponse>(`${this.apiUrl}/wishlist?${params}`)
      .pipe(
        map((response) => response.data),
        catchError((error) =>
          this.handleError(error, 'Failed to load wishlist items')
        )
      );
  }

  clearWishlist(): Observable<any> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    return this.http.delete(`${this.apiUrl}/wishlist/clear`).pipe(
      tap(() => {
        this.clearWishlistData();
      }),
      catchError((error) => this.handleError(error, 'Failed to clear wishlist'))
    );
  }

  isInWishlist(listingId: string): boolean {
    const status = this.getWishlistStatus(listingId);
    return status.in_wishlist;
  }

  getWishlistStatus(listingId: string): {
    in_wishlist: boolean;
    wishlist_id: string | null;
  } {
    const status = this.wishlistStatusSubject.value[listingId];
    return status || { in_wishlist: false, wishlist_id: null };
  }

  getCurrentSummary(): WishlistSummary {
    return this.wishlistSummarySubject.value;
  }

  refreshWishlistData(): void {
    if (this.authService.isLoggedIn()) {
      this.summaryLoadSubject.next(true);
    }
  }

  private updateLocalWishlistStatus(
    listingId: string,
    status: { in_wishlist: boolean; wishlist_id: string | null }
  ): void {
    const currentStatus = { ...this.wishlistStatusSubject.value };

    if (status.in_wishlist && status.wishlist_id) {
      currentStatus[listingId] = status;
    } else {
      delete currentStatus[listingId];
    }

    this.wishlistStatusSubject.next(currentStatus);
    this.saveOptimizedStatusToCache(currentStatus);
  }

  private updateMultipleWishlistStatus(wishlistStatus: WishlistStatus): void {
    const currentStatus = { ...this.wishlistStatusSubject.value };

    Object.entries(wishlistStatus).forEach(([listingId, status]) => {
      if (status.in_wishlist && status.wishlist_id) {
        currentStatus[listingId] = status;
      } else {
        delete currentStatus[listingId];
      }
    });

    this.wishlistStatusSubject.next(currentStatus);
    this.saveOptimizedStatusToCache(currentStatus);
  }

  private saveOptimizedStatusToCache(status: WishlistStatus): void {
    if (!this.authService.isLoggedIn()) return;

    try {
      const optimizedStatus: OptimizedWishlistStatus = {};

      Object.entries(status).forEach(([listingId, statusData]) => {
        if (statusData.in_wishlist && statusData.wishlist_id) {
          optimizedStatus[listingId] = statusData.wishlist_id;
        }
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(optimizedStatus));
      this.updateCacheTimestamp();
    } catch (error) {
      console.warn('Failed to save wishlist status to cache:', error);
    }
  }

  private loadCachedWishlistData(): void {
    if (!this.authService.isLoggedIn()) return;

    try {
      const cachedStatus = localStorage.getItem(this.STORAGE_KEY);
      const cachedSummary = localStorage.getItem(this.SUMMARY_STORAGE_KEY);

      if (cachedStatus && this.isCacheValid()) {
        const optimizedStatus: OptimizedWishlistStatus =
          JSON.parse(cachedStatus);

        const fullStatus: WishlistStatus = {};
        Object.entries(optimizedStatus).forEach(([listingId, wishlistId]) => {
          fullStatus[listingId] = {
            in_wishlist: true,
            wishlist_id: wishlistId,
          };
        });

        this.wishlistStatusSubject.next(fullStatus);
      }

      if (cachedSummary) {
        const summary = JSON.parse(cachedSummary);
        this.wishlistSummarySubject.next(summary);
        this.summaryLoaded = true;
      }
    } catch (error) {
      console.warn('Failed to load cached wishlist data:', error);
      this.clearCache();
    }
  }

  private isCacheValid(listingId?: string): boolean {
    try {
      const expiryTime = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (!expiryTime) return false;

      return Date.now() < parseInt(expiryTime);
    } catch {
      return false;
    }
  }

  private updateCacheTimestamp(): void {
    try {
      const expiryTime = Date.now() + this.CACHE_DURATION;
      localStorage.setItem(this.CACHE_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.warn('Failed to update cache timestamp:', error);
    }
  }

  private setupCacheCleanup(): void {
    setInterval(() => {
      if (!this.isCacheValid()) {
        this.clearCache();
      }
    }, 60 * 60 * 1000);
  }

  refreshWishlistSummary(): void {
    this.refreshWishlistData();
  }

  private clearWishlistData(): void {
    this.wishlistStatusSubject.next({});
    this.wishlistSummarySubject.next({ total: 0, by_category: {} });
    this.summaryLoaded = false;
    this.summaryLoading = false;
    this.clearCache();
  }

  private saveSummaryToCache(summary: WishlistSummary): void {
    if (!this.authService.isLoggedIn()) return;

    try {
      localStorage.setItem(this.SUMMARY_STORAGE_KEY, JSON.stringify(summary));
    } catch (error) {
      console.warn('Failed to save wishlist summary to cache:', error);
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.SUMMARY_STORAGE_KEY);
      localStorage.removeItem(this.CACHE_EXPIRY_KEY);
    } catch (error) {
      console.warn('Failed to clear wishlist cache:', error);
    }
  }

  private handleError(error: any, defaultMessage: string): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.status === 401 || error.status === 403) {
      errorMessage = 'Authentication required. Please log in.';
    } else if (error.status === 404) {
      errorMessage = 'Item not found.';
    } else if (error.status === 409) {
      errorMessage = 'Item is already in your wishlist.';
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    }

    console.error('Wishlist service error:', error);
    return throwError(() => new Error(errorMessage));
  }

  getCategoryFromTableName(tableName: string): string {
    if (
      tableName.includes('automobile') ||
      tableName.includes('motorcycle') ||
      tableName.includes('commercial') ||
      tableName.includes('oldtimer') ||
      tableName.includes('nautical') ||
      tableName.includes('spare_parts') ||
      tableName.includes('campers') ||
      tableName.includes('crashed') ||
      tableName.includes('construction') ||
      tableName.includes('agriculture')
    ) {
      return 'vehicles';
    }

    if (
      tableName.includes('house') ||
      tableName.includes('apartment') ||
      tableName.includes('land') ||
      tableName.includes('office') ||
      tableName.includes('garage') ||
      tableName.includes('luxury') ||
      tableName.includes('room') ||
      tableName.includes('roommate')
    ) {
      return 'real-estate';
    }

    if (
      tableName.includes('professional') ||
      tableName.includes('maintenance') ||
      tableName.includes('personal_care') ||
      tableName.includes('events') ||
      tableName.includes('education') ||
      tableName.includes('transport')
    ) {
      return 'jobs';
    }

    return 'marketplace';
  }

  canUseWishlist(): boolean {
    return this.authService.isLoggedIn();
  }
}
